import Web3 from 'web3';
import { getChainReadRpcUrls } from '../../constants/evmChain.registry';
import { resolveWalletReadRpcUrls } from './linkedWallet.utils';
import { logNonCustodialBalance } from './nonCustodialBalance.debug';

const READ_CACHE_TTL_MS = 10000;
const readCache = new Map();
const failedRpcUrls = new Set();

const createReadWeb3 = (rpcUrl) =>
  new Web3(new Web3.providers.HttpProvider(rpcUrl, { timeout: 12000 }));

export { getChainReadRpcUrls };

const isTransportError = (error) => {
  const message = String(error?.message ?? '').toLowerCase();
  return (
    error?.code === 'NETWORK_ERROR' ||
    /networkerror|failed to fetch|cors|cross-origin|network request failed|connection|timeout/i.test(
      message,
    )
  );
};

export const withReadCache = async (cacheKey, operation, { useCache = true } = {}) => {
  if (useCache) {
    const cached = readCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.value;
    }
  }

  const value = await operation();
  if (useCache) {
    readCache.set(cacheKey, {
      value,
      expiresAt: Date.now() + READ_CACHE_TTL_MS,
    });
  }
  return value;
};

export const invalidateReadCache = () => {
  readCache.clear();
};

const resolveReadRpcUrlList = (chainId, wallet) => {
  const apiRpcUrls = wallet ? resolveWalletReadRpcUrls(wallet) : [];
  const staticRpcUrls = Number.isFinite(chainId) ? getChainReadRpcUrls(chainId) : [];

  return [...apiRpcUrls, ...staticRpcUrls].filter(
    (url, index, arr) => url && !failedRpcUrls.has(url) && arr.indexOf(url) === index,
  );
};

/**
 * Run a read-only Web3 operation via public RPC.
 * Prefers linked-wallets API rpcUrl, then static registry, then MetaMask provider.
 */
export const callWithReadWeb3 = async (walletWeb3, chainIdHint, operation, options = {}) => {
  const chainId = Number(chainIdHint);
  const wallet = options?.wallet ?? null;

  if (!Number.isFinite(chainId)) {
    return operation(walletWeb3, { viaPublicRpc: false, rpcUrl: null, source: 'wallet' });
  }

  const rpcUrls = resolveReadRpcUrlList(chainId, wallet);

  for (const rpcUrl of rpcUrls) {
    try {
      const readWeb3 = createReadWeb3(rpcUrl);
      const result = await operation(readWeb3, {
        viaPublicRpc: true,
        rpcUrl,
        source: wallet && resolveWalletReadRpcUrls(wallet)[0] === rpcUrl ? 'api' : 'registry',
      });
      logNonCustodialBalance('readRpc:success', { chainId, rpcUrl, source: wallet ? 'api-first' : 'registry' });
      return result;
    } catch (error) {
      if (!isTransportError(error)) {
        throw error;
      }
      failedRpcUrls.add(rpcUrl);
      logNonCustodialBalance('readRpc:transport-failed', {
        chainId,
        rpcUrl,
        message: error?.message,
      });
    }
  }

  logNonCustodialBalance('readRpc:fallback-to-wallet', { chainId });
  return operation(walletWeb3, { viaPublicRpc: false, rpcUrl: null, source: 'wallet' });
};
